// Scrollable document renderer for a raw PDF (added via the dashboard's
// "Document" upload mode). A browser can't show a PDF inline without its viewer
// chrome, so we rasterize every page to its own <canvas> with pdf.js and stack
// them vertically into one continuous, scrollable surface — no toolbar, no
// scrollbars-chrome, just the document. On a touch kiosk the operator/customer
// scrolls through the whole document; in a multi-item rotation the slot still
// surrenders after the item duration.
//
// (PDFs imported "from a folder" / "Convert pages to images" become individual
// image items upstream and never reach this component — they take the
// ImageDisplay path. This is only the single-document case.)
//
// pdf.js is resolved from the hoisted workspace dependency (the signage app's
// PdfThumbnail uses the same package + worker import); the worker keeps parsing
// off the main thread. Pages render lazily one after another so a long document
// doesn't spike memory rasterizing everything up front.

import { useEffect, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import * as pdfjsLib from 'pdfjs-dist'
// Vite ?worker virtual import — typed via vite/client in vite-env.d.ts.
import PdfJsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?worker'
import { absoluteUrl } from '../lib/contentUrl'
import ContentErrorScreen from './ContentErrorScreen'

// IMPORTANT: do NOT set a single global GlobalWorkerOptions.workerPort here.
// pdf.js caches one PDFWorker per port (PDFWorker.create keys on the port), so
// a shared global port means every PdfDisplay instance resolves to the SAME
// worker. During the carousel's 600ms crossfade two PDF tiles are mounted at
// once; the outgoing tile's task.destroy() flags that shared worker
// `_pendingDestroy`, and the incoming tile's getDocument() then throws
// "PDFWorker.create - the worker is being destroyed." (the all-PDF-playlist
// failure). Instead each instance below spins up its OWN worker + port, so
// destroying one document can never abort another.

interface Props {
  url:        string
  durationMs: number
  // Kept for dispatch parity with the other renderers; a scrolled document
  // always fits page width, so cover/contain doesn't apply here.
  scaling?:   'default' | 'fit'
  onFinished: () => void
  onError:    (msg: string) => void
}

export default function PdfDisplay({ url, durationMs, onFinished, onError }: Props) {
  const fullUrl = absoluteUrl(url)
  const containerRef = useRef<HTMLDivElement>(null)
  const [hasError, setHasError] = useState(false)
  // True once the first page is actually on screen — mirrors VideoDisplay's
  // `isPlaying` gate. Without this the rotation timer (below) started
  // counting from MOUNT, not from when the document became visible, so a
  // slow-loading multi-page PDF ate its whole configured duration just
  // opening: the operator's "show for 10s" became "load for 6s, show the
  // finished render for 4s, cut" — read as the item lagging in, then barely
  // showing before advancing.
  const [loaded, setLoaded] = useState(false)
  const finishedRef = useRef(false)

  const advance = () => {
    if (finishedRef.current) return
    finishedRef.current = true
    onFinished()
  }

  // Load + render every page into the scroll container. A bad / password-locked
  // file reports the error and the rotation timer below still advances the slot
  // so it can't freeze a multi-item rotation.
  useEffect(() => {
    let cancelled = false
    let task: ReturnType<typeof pdfjsLib.getDocument> | null = null
    // Dedicated worker for THIS instance only — see the header note. A fresh
    // port per mount means pdf.js caches a distinct PDFWorker, so a sibling
    // tile's teardown can't poison this one. (pdfjs-dist mistypes the ctor
    // `port` as null|undefined — its JSDoc lacks the real Worker type, cf.
    // `get port(): Worker` — so cast through unknown.)
    const worker = new pdfjsLib.PDFWorker(
      { port: new PdfJsWorker() } as unknown as { port?: null },
    )
    finishedRef.current = false
    setHasError(false)
    setLoaded(false)
    const container = containerRef.current
    if (container) container.replaceChildren()

    ;(async () => {
      try {
        task = pdfjsLib.getDocument({ url: fullUrl, worker })
        const doc = await task.promise
        if (cancelled) return
        // Fit each page to the container width; DPR-aware (capped at 2) so
        // hi-DPI screens stay crisp without an unbounded canvas footprint.
        const dpr = Math.min(window.devicePixelRatio || 1, 2)
        const cssWidth = container?.clientWidth || 1920
        for (let n = 1; n <= doc.numPages; n++) {
          if (cancelled) return
          const p = await doc.getPage(n)
          if (cancelled) return
          const base = p.getViewport({ scale: 1 })
          const viewport = p.getViewport({ scale: (cssWidth / base.width) * dpr })
          const canvas = document.createElement('canvas')
          canvas.width = Math.floor(viewport.width)
          canvas.height = Math.floor(viewport.height)
          // Lay pages out full-width, top to bottom — the container scrolls.
          canvas.style.width = '100%'
          canvas.style.height = 'auto'
          canvas.style.display = 'block'
          container?.appendChild(canvas)
          await p.render({ canvas, viewport }).promise
          // First page is now actually on screen — start the rotation clock
          // from here, not from mount (see `loaded`'s comment above).
          if (n === 1 && !cancelled) setLoaded(true)
        }
      } catch (e) {
        if (cancelled) return
        setHasError(true)
        // Surface the REAL reason (CORS abort / Range / InvalidPDFException /
        // PasswordException / worker conflict) instead of swallowing it. Without
        // this the failure is an invisible "Something is wrong" — the generic
        // string below went to the device log and Sentry never heard about it.
        const reason = e instanceof Error ? `${e.name}: ${e.message}` : String(e)
        onError(`PDF open failed (${reason}): ${fullUrl}`)
        try {
          Sentry.captureException(
            e instanceof Error ? e : new Error(`PDF open failed: ${reason}`),
            { tags: { content: 'pdf' }, extra: { url: fullUrl } },
          )
        } catch { /* sentry not configured — fall through */ }
      }
    })()

    return () => {
      cancelled = true
      // Await the document teardown BEFORE terminating the worker (pdf.js asks
      // for this in the very error we're fixing), then kill the worker so its
      // thread + port are released. Because we passed our own `worker`,
      // loadingTask.destroy() doesn't own it — we terminate it explicitly.
      ;(async () => {
        try { await task?.destroy() } catch { /* noop */ }
        try { worker.destroy() } catch { /* noop */ }
      })()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullUrl])

  // Rotation advance: a PDF sharing the screen with other items surrenders its
  // slot after durationMs of actually being ON SCREEN (gated on `loaded`, not
  // mount) — see `loaded`'s comment above. On a single-item screen advance()
  // is effectively a no-op (nowhere to advance to) so the scrollable document
  // just stays up.
  useEffect(() => {
    if (!loaded) return
    const t = window.setTimeout(advance, durationMs)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, fullUrl, durationMs])

  // Safety valve: if the document never finishes loading its first page
  // (hung network, huge file, worker deadlock), don't stall the whole
  // rotation on it forever — same durationMs+60s pattern VideoDisplay uses.
  useEffect(() => {
    const t = window.setTimeout(advance, durationMs + 60_000)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullUrl, durationMs])

  if (hasError) return <ContentErrorScreen />

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 bg-black overflow-y-auto overflow-x-hidden"
    />
  )
}
